import { GenericContract } from 'eth-components/ant/generic-contract';
import { useBalance, useEthersAdaptorFromProviderOrSigners, useBlockNumber } from 'eth-hooks2';
import { useEthersAppContext } from 'eth-hooks2/context';
import { useDexEthPrice } from 'eth-hooks2/dapps';
import { NextPage } from 'next';
import { ReactElement } from 'react';

import FriendlyPage3 from '../friendly/FriendlyPage3';

import { MainPageFooter, MainPageHeader, createTabsAndPages, TContractPageList } from '.';

import { useLoadAppContracts, useConnectAppContracts, useAppContracts } from '~common/components/context';
import { useCreateAntNotificationHolder } from '~common/components/hooks/useAntNotification';
import { useScaffoldAppProviders } from '~common/components/hooks/useScaffoldAppProviders';
import {
  AVAILABLE_NETWORKS_DEFINITIONS,
  CONNECT_TO_BURNER_AUTOMATICALLY,
  LOCAL_PROVIDER,
  MAINNET_PROVIDER,
  INFURA_ID,
} from '~~/config/nextjsApp.config';
import { TAppProps } from '~~/models/TAppProps';

/** ********************************
 * ⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️
 * See ./config/app.config.ts for configuration, such as TARGET_NETWORK
 * See ../common/src/config/appContracts.config.ts and ../common/src/config/externalContracts.config.ts to configure your contracts
 * See pageList variable below to configure your pages
 * See ../common/src/config/web3Modal.config.ts to configure the web3 modal
 * ⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️⛳️
 * ******************************** */

interface IMainPageProps {
  pageName: string;
  children?: ReactElement;
  appProps: TAppProps;
}

/**
 * The main component
 * @returns
 */
export const MainPage: NextPage<IMainPageProps> = (props) => {
  // passed in by nextjs getInitalProps
  const appProps: TAppProps = props.appProps;

  const notificationHolder = useCreateAntNotificationHolder();

  // -----------------------------
  // Providers, signers & wallets
  // -----------------------------
  // 🛰 providers
  // see useLoadProviders.ts for everything to do with loading the right providers
  const scaffoldAppProviders = useScaffoldAppProviders({
    targetNetworks: AVAILABLE_NETWORKS_DEFINITIONS,
    connectToBurnerAutomatically: CONNECT_TO_BURNER_AUTOMATICALLY,
    localProvider: LOCAL_PROVIDER,
    mainnetProvider: MAINNET_PROVIDER,
    infuraId: INFURA_ID,
  });

  // 🦊 Get your web3 ethers context from current providers
  const ethersAppContext = useEthersAppContext();

  // -----------------------------
  // Load Contracts
  // -----------------------------
  // 🛻 load contracts
  useLoadAppContracts();
  // 🏭 connect to contracts for mainnet network & signer
  const [mainnetAdaptor] = useEthersAdaptorFromProviderOrSigners(MAINNET_PROVIDER);
  useConnectAppContracts(mainnetAdaptor);

  // -----------------------------
  // Hooks use and examples
  // -----------------------------
  // 🎉 Console logs & More hook examples:
  // 🚦 disable this hook to stop console logs
  // 🏹🏹🏹 go here to see how to use hooks!
  // useScaffoldHooksExamples(scaffoldAppProviders);
  const [blockNumber] = useBlockNumber(scaffoldAppProviders.mainnetAdaptor?.provider);
  // -----------------------------
  // These are the contracts!
  // -----------------------------

  // init contracts
  const merkleOrderbookFactory = useAppContracts('MerkleOrderbookFactory', ethersAppContext.chainId);
  const sGDToken = useAppContracts('FiatTokenV1', ethersAppContext.chainId);
  const router = useAppContracts('OrderBookRouter', ethersAppContext.chainId);
  const cashWallet = useAppContracts('CashWallet', ethersAppContext.chainId);
  const policyWallet = useAppContracts('PolicyWallet', ethersAppContext.chainId);
  // keep track of a variable from the contract in the local React state:
  // const [purpose, update] = useContractReader(
  //   yourContract,
  //   yourContract?.purpose,
  //   [],
  //   yourContract?.filters.SetPurpose()
  // );

  // 📟 Listen for broadcast events
  // stupid hack
  // const [setPurposeEvents] = useEventListener(yourContract, 'SetPurpose', blockNumber - 50);

  // -----------------------------
  // .... 🎇 End of examples
  // -----------------------------
  // 💵 This hook will get the price of ETH from 🦄 Uniswap:
  const [ethPrice] = useDexEthPrice(
    scaffoldAppProviders.mainnetAdaptor?.provider,
    ethersAppContext.chainId !== 1 ? scaffoldAppProviders.currentTargetNetwork : undefined
  );

  // 💰 this hook will get your balance
  const [yourCurrentBalance] = useBalance(ethersAppContext.account);

  // -----------------------------
  // 📃 App Page List
  // -----------------------------
  // This is the list of tabs and their contents
  const pageList: TContractPageList = {
    mainPage: {
      name: 'MerkleOrderBookFactory',
      content: (
        <GenericContract
          contractName="MerkleOrderBookFactory"
          contract={merkleOrderbookFactory}
          mainnetAdaptor={scaffoldAppProviders.mainnetAdaptor}
          blockExplorer={scaffoldAppProviders.currentTargetNetwork.blockExplorer}
        />
      ),
    },
    pages: [
      {
        name: '22FriendlyPage',
        content: (
          <FriendlyPage3
            ethersAppContext={ethersAppContext}
            merkleOrderbookFactory={merkleOrderbookFactory}
            router={router}
            cashWallet={cashWallet}
            policyWallet={policyWallet}
            cashToken={sGDToken}
            mainnetAdaptor={scaffoldAppProviders.mainnetAdaptor}
          />
        ),
      },
      {
        name: 'SGDToken',
        content: (
          <GenericContract
            contractName="FiatTokenV1"
            contract={sGDToken}
            mainnetAdaptor={scaffoldAppProviders.mainnetAdaptor}
            blockExplorer={scaffoldAppProviders.currentTargetNetwork.blockExplorer}></GenericContract>
        ),
      },
    ],
  };
  const { tabMenu, pages } = createTabsAndPages(pageList);
  const RouteNotFound = <h3 className="p-10 mt-10 text-xl">Route Not Found </h3>;

  // -----------------------------
  // 📃 Render the react components
  // -----------------------------

  return (
    <div>
      <MainPageHeader scaffoldAppProviders={scaffoldAppProviders} price={ethPrice} appProps={appProps} />
      {tabMenu}
      {pages[props.pageName] ?? RouteNotFound}
      <MainPageFooter scaffoldAppProviders={scaffoldAppProviders} price={ethPrice} appProps={appProps} />
      <div className="absolute bg-slate-600">{notificationHolder}</div>
    </div>
  );
};
